// "Descargar Matriz del día" — the panel's Matriz download, for the one date "Procesar el día" just
// ran. The macro ended its process by handing over the Matriz; this is that last step.
//
// It asks for what the panel asks for (`filterOffices` for the date, `getExtractionFields` for the
// columns and how the workbook looks) and builds the rows with the panel's own `filaDeCierre`, so
// the two downloads cannot disagree. Rows are cut to the ones the person may export
// (`filasExportables`), as the panel does.
//
// B-175 — OBSERVACIONES is the team's notes on each office's alerts (`notasDelEquipo`), so the day's
// alerts are read with it (`getCierreAlertsBatch`); a failed batch fails the download rather than
// handing over a report with its justifications silently left out.
//
// `matrizDeRango` is the same workbook for every day of a range — "Cierres por fecha"'s download.
// It reads the range one day at a time, each exactly as `matrizDelDia` reads its day, so a day
// always fits one `filterOffices` page and one alerts batch (the backend's own cap is 500 closings).

import { filterOffices, getCierreAlertsBatch, getExtractionFields } from "../ApiCalls/mantisBusiness";
import { columnasDeLista, columnasDeResumen, filaDeCierre, restarDias } from "./cierrePanel";
import { hojaDeMatriz } from "./cierreExcel";
import { filasExportables } from "./cierrePermisos";

// One day of one client fits in a page: Bancamía has 140 offices, the panel's own limit is 500.
const LIMITE_FILAS = 500;

// The longest range one download covers: a year of Bancamía is ~51.000 rows.
export const MAX_DIAS_MATRIZ = 366;

// How many days are read at once.
const DIAS_A_LA_VEZ = 4;

// The columns and how the workbook looks — the same for every day.
async function camposDeMatriz(token, mantisBusinessId, clientCode) {
  const campos = await getExtractionFields(token, mantisBusinessId, clientCode);
  if (!campos?.fields) throw new Error("No se pudieron leer las columnas de la Matriz");
  return {
    columnas: columnasDeLista(campos.fields, campos.list),
    resumen: columnasDeResumen(campos.fields),
    config: campos.export || null,
  };
}

// One day's rows, in the order the backend answers them, with their alerts.
async function filasDelDia({ token, mantisBusinessId, clientCode, fecha, columnas, resumen, nombrar }) {
  const response = await filterOffices(
    token,
    { startDate: fecha, endDate: fecha },
    LIMITE_FILAS,
    mantisBusinessId,
    "brinks",
    clientCode
  );
  if (!response?.success) throw new Error("No se pudieron leer los cierres del día");

  const crudas = response.data || [];
  // A full page may have left closings out; a Matriz short of offices is worse than none.
  if (crudas.length >= LIMITE_FILAS) throw new Error(`El ${fecha} tiene más cierres de los que se pueden descargar`);

  const closingIds = [...new Set(crudas.map((r) => r.closing_id).filter(Boolean))];
  let alertasPorCierre = {};
  if (closingIds.length) {
    const alertas = await getCierreAlertsBatch(token, mantisBusinessId, closingIds);
    if (!alertas?.success) throw new Error("No se pudieron leer las alertas del día");
    alertasPorCierre = Object.fromEntries((alertas.data || []).map((e) => [e.closingId, e.alerts || []]));
  }
  return crudas.map((row) => filaDeCierre(row, columnas, resumen, alertasPorCierre, nombrar));
}

const exportables = (acceso, clientCode, filas) =>
  filasExportables(acceso, clientCode, filas, (f) => ({ oficina: f.raw.codigo_oficina, region: f.regional }));

/**
 * @param {object} options
 * @param {string} options.token
 * @param {string} options.mantisBusinessId
 * @param {string} options.clientCode
 * @param {string} options.fecha      `YYYY-MM-DD`, the processed day
 * @param {*}      options.acceso     `/access/me`, as `useAccesoCierres` hands it
 * @param {function} [options.nombrar] names an alert for the observations column
 * @returns {Promise<object>} `hojaDeMatriz`'s sheet, ready for `descargarMatriz`
 */
export async function matrizDelDia({ token, mantisBusinessId, clientCode, fecha, acceso, nombrar }) {
  const { columnas, resumen, config } = await camposDeMatriz(token, mantisBusinessId, clientCode);
  const filas = await filasDelDia({ token, mantisBusinessId, clientCode, fecha, columnas, resumen, nombrar });
  return hojaDeMatriz({ columnas, filas: exportables(acceso, clientCode, filas), config });
}

/**
 * The days from `desde` to `hasta`, both included, oldest first — or why the range cannot be
 * downloaded, said for the operator.
 *
 * @returns {{ dias: string[] } | { error: string }}
 */
export function diasDelRango(desde, hasta) {
  if (!desde || !hasta) return { error: "Elige la fecha inicial y la final para descargar la Matriz." };
  if (desde > hasta) return { error: "La fecha inicial es posterior a la final." };
  const dias = [];
  for (let dia = desde; dia <= hasta; dia = restarDias(dia, -1)) {
    dias.push(dia);
    if (dias.length > MAX_DIAS_MATRIZ) return { error: "La Matriz se descarga de máximo un año a la vez." };
  }
  return { dias };
}

/**
 * The Matriz of every day from `desde` to `hasta`, oldest day first.
 *
 * @param {object}   options               as `matrizDelDia`, with `desde`/`hasta` for `fecha`
 * @param {function} [options.onAvance]   `(díasLeídos, díasTotales)` after each day
 * @returns {Promise<object>} `hojaDeMatriz`'s sheet, ready for `descargarMatriz`
 */
export async function matrizDeRango({ token, mantisBusinessId, clientCode, desde, hasta, acceso, nombrar, onAvance }) {
  const rango = diasDelRango(desde, hasta);
  if (rango.error) throw new Error(rango.error);
  const { dias } = rango;

  const { columnas, resumen, config } = await camposDeMatriz(token, mantisBusinessId, clientCode);

  const porDia = new Array(dias.length);
  let leidos = 0;
  let siguiente = 0;
  const trabajador = async () => {
    while (siguiente < dias.length) {
      const i = siguiente;
      siguiente += 1;
      porDia[i] = await filasDelDia({ token, mantisBusinessId, clientCode, fecha: dias[i], columnas, resumen, nombrar });
      leidos += 1;
      if (onAvance) onAvance(leidos, dias.length);
    }
  };
  await Promise.all(Array.from({ length: Math.min(DIAS_A_LA_VEZ, dias.length) }, trabajador));

  const filas = porDia.flat();
  return hojaDeMatriz({ columnas, filas: exportables(acceso, clientCode, filas), config });
}
